import moment from "moment";
import firebase from "firebase/app";
import { ServiceStatus } from "@/enums/ServiceStatus";
import Loader from "@/components/loader/Loader.vue";
import { ResidentStatus } from "@/enums/ResidentStatus";
import { ResidentLogs } from "@/enums/ResidentLogs";
import { EventsTypes } from "@/enums/EventsTypes";
import { FacilitySettingsModel } from "@/models/FacilitySettings";
import { calculateServicesSpentMoney } from "@/utils/calculateServicesSpentMoney";

export default {
  name: "ReportOrganisationHealthDashboard",
  data() {
    return {
      period: "week",
      startDate: moment().subtract(1, "week").toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      loading: true,
      serviceStatus: ServiceStatus,
      selectedReportFacility: null,
      facilitiesResidents: [],
      facilitiesRequests: [],
      facilitiesResidentsActivity: {},
      facilitiesLifestyleEvents: [],
      facilitiesLifestyleEventsPopular: [],
      facilitiesMaintenance: {},
      periods: [
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Week"], value: "week" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Month"], value: "month" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Year"], value: "year" }
      ],
      picker: {
        start: false,
        end: false
      },
      headerPremium: [
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Facility"],
          value: "name",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Name"],
          value: "service",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Provider"],
          value: "provider",
        }
      ],
      headerLifestyle: [
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Facility"],
          value: "name",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Program"],
          value: "program",
        },
      ]
    }
  },
  components: {
    Loader
  },
  computed: {
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    facilities() {
      return this.$store.state.facilityModule.facilities;
    },
    facilitiesList() {
      return [{ id: null, name: 'All' }].concat(this.facilities);
    },
    facilitiesIds() {
      return this.$store.state.facilityModule.facilities.map(x => x.id);
    },
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat)
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat)
    },
    premiumServicesPopular() {
      const result = this.facilitiesRequests.map(item => {
        const servicesBooked = item.requests.reduce((total: any, current: any) => {
          const object = total.find(x => x.name === current.serviceName);

          if (object) {
            object.count++
          } else {
            total.push({
              providerName: current.providerName,
              name: current.serviceName,
              count: 1,
            })
          }
          return total;
        }, []);
        const serviceMaxValue = servicesBooked.reduce((total, curr) => total.count > curr.count ? total : curr, {});

        return {
          name: item.name,
          provider: serviceMaxValue.providerName,
          service: serviceMaxValue.name,
        }
      });

      return result
    },
    lifestyleEvents() {
      const lifestyleEvents = {}
      this.facilitiesLifestyleEvents.forEach(item => {
        lifestyleEvents[item.name] = item.events.length;
      })
      return lifestyleEvents;
    },
    socialClubs() {
      return this.$store.state.socialClubModule.socialClubs;
    },
    facilityCurrency() {
      return this.$store.getters.facilityCurrency;
    },
    servicesBooked() {
      if (!this.selectedReportFacility) {
        return this.facilitiesRequests.map(facilityStat => {
          const sum = calculateServicesSpentMoney(facilityStat.requests);
          const servicesBooked = facilityStat.requests.reduce((total: any, current: any) => {
            if (!total.includes(current.serviceName)) {
              return [...total, current.serviceName];
            }
            return total;
          }, []);

          return {
            id: facilityStat.id,
            name: facilityStat.name,
            servicesBooked: servicesBooked,
            sum: sum
          };
        });
      }

      const facilityStat = this.facilitiesRequests.find(x => x.id === this.selectedReportFacility);

      if (!facilityStat) return [];

      const sum = calculateServicesSpentMoney(facilityStat.requests);

      const servicesBooked = facilityStat.requests.reduce((total: any, current: any) => {
        if (!total.includes(current.serviceName)) {
          return [...total, current.serviceName];
        }
        return total;
      }, []);

      return [{
        id: facilityStat.id,
        name: facilityStat.name,
        servicesBooked: servicesBooked,
        sum: sum
      }];
    }
  },
  methods: {
    routeToReports() {
      this.$router.push("/reports");
    },
    handleChangePeriod(period) {
      this.startDate = moment()
        .subtract(1, period)
        .toISOString()
        .substring(0, 10);

      this.endDate = moment()
        .toISOString()
        .substring(0, 10);

      this.getEventsByDates();
    },
    async getEventsByDates() {
      this.loading = true;
      this.facilitiesRequests = [];
      this.facilitiesLifestyleEvents = [];
      this.facilitiesResidentsActivity = {};
      this.facilitiesMaintenance = {};

      await this.getLifestyleEvents();
      await this.lifestyleEventsPopular();
      if (this.facility.maintenanceEnabled) {
        await this.getMaintenanceRequests();
      }
      await this.getActiveResidents();
      await this.getServicesRequestsApproveCompleted();

      //this.servicesBooked = this.getServicesBooked(this.selectedReportFacility.id)

      this.loading = false;
    },
    async getServicesRequestsApproveCompleted() {
      for (let i = 0; i < this.facilitiesIds.length; i++) {
        const facilityId = this.facilitiesIds[i];
        await firebase
          .firestore()
          .collection("serviceRequests")
          .where("facilityId", "==", facilityId)
          .where("createdDateTime", ">=", moment(this.startDate).toDate())
          .get()
          .then(querySnapshot => {
            const resList: Array<unknown> = [];
            querySnapshot.docs.forEach(x => {
              const thisRes = x.data();
              if ([this.serviceStatus.Completed, this.serviceStatus.Approved].includes(thisRes.status)) {
                thisRes.id = x.id;
                resList.push(thisRes);
              }
            });
            this.facilitiesRequests.push({
              id: this.facilities[i].id,
              name: this.facilities[i].name,
              requests: resList
            })
          }).catch((e) => console.error(e))
      }
    },
    async getResidentsIds() {
      const facilitiesResidentsPromises = this.facilitiesIds.map(async (facilityId, i) => {
        const querySnapshot = await firebase
          .firestore()
          .collection("residents")
          .where("facilityId", "==", facilityId)
          .where("status", "==", ResidentStatus.ActiveResident)
          .get()
        const resList = querySnapshot.docs.map((resident) => resident.id);
        return {
          name: this.facilities[i].name,
          residentsIds: resList,
        };
      });

      const facilitiesResidents = await Promise.all(facilitiesResidentsPromises);
      this.facilitiesResidents = facilitiesResidents;
    },
    async getActiveResidents() {
      const facilitiesResidentsActivity = {};
      for (let i = 0; i < this.facilitiesIds.length; i++) {
        const facilityId = this.facilitiesIds[i];
        const facilityName = this.facilities[i].name;
        const facility = {
          count: 0
        };
        if (this.facilitiesResidents[i] && this.facilitiesResidents[i].residentsIds) {
          const residentsIds = this.facilitiesResidents[i].residentsIds;
          const residentsPromises = residentsIds.map(async (residentId) => {
            const querySnapshot = await firebase
              .firestore()
              .collection("residents")
              .doc(residentId)
              .collection("events")
              .where("facilityId", "==", facilityId)
              .where("eventDateTime", ">=", moment(this.startDate).toDate())
              .where("eventType", "==", ResidentLogs.OpenApp)
              .get()

            if (querySnapshot.docs.length) {
              facility.count++;
            }
          });
          await Promise.all(residentsPromises);
        }
        facilitiesResidentsActivity[facilityName] = facility.count;
      }
      this.facilitiesResidentsActivity = facilitiesResidentsActivity;
    },
    async getLifestyleEvents() {
      for (let i = 0; i < this.facilitiesIds.length; i++) {
        const facilityId = this.facilitiesIds[i];
        const facilityName = this.facilities[i].name;

        await firebase
          .firestore()
          .collection("calender")
          .where("facilityId", "==", facilityId)
          .where("start", ">=", moment(this.startDate).toDate())
          .where("start", "<=", moment(this.endDate).toDate())
          .where("type", "==", EventsTypes.LifestylePrograms)
          .get()
          .then(querySnapshot => {

            const resList: Array<any> = [];

            querySnapshot.docs.forEach((x: any) => {
              const thisRes = x.data();
              thisRes.id = x.id;
              resList.push(thisRes);
            });

            this.facilitiesLifestyleEvents.push({
              name: facilityName,
              events: resList
            });
          }).catch((e) => console.error(e))
      }
    },
    async lifestyleEventsPopular() {
      this.facilitiesLifestyleEventsPopular = this.facilitiesLifestyleEvents.map(item => {
        const lifestyleEvent = item.events.reduce((total: any, current: any) => {
          const object = total.find(x => x.programId === current.invitees[0]);
          const program = this.socialClubs.find(x => x.id === current.invitees[0]);

          if (object) {
            object.count++
          } else {
            total.push({
              program: program ? program.name : "All",
              count: 1,
            })
          }
          return total;
        }, []);
        const serviceMaxValue = lifestyleEvent.reduce((total, curr) => total.count > curr.count ? total : curr, {});

        return {
          name: item.name,
          program: serviceMaxValue.program,
        }
      });
    },
    async getMaintenanceRequests() {
      const queries = this.facilitiesIds.map(async (facilityId, i) => {
        const facilityName = this.facilities[i].name;
        const querySnapshot = await firebase.firestore()
          .collection("maintenanceRequests")
          .where("facilityId", "==", facilityId)
          .where("status", "in", ["complete", "new"])
          .where("dateTime", ">=", moment(this.startDate).toDate())
          .get();
        this.facilitiesMaintenance[facilityName] = querySnapshot.docs.length;
      });

      await Promise.all(queries).catch((e) => console.error(e));
    }
  },
  async created() {
    await this.$store.dispatch("getFacility");
    await this.$store.dispatch("getFacilities");
    await this.getServicesRequestsApproveCompleted();
    await this.$store.dispatch("getSocialClubs");
    if (this.facility.maintenanceEnabled) {
      await this.getMaintenanceRequests();
    }
    await this.getResidentsIds();
    await this.getActiveResidents();
    await this.getLifestyleEvents();
    await this.lifestyleEventsPopular();

    this.loading = false;
  }
}